<template>
  <div class="options">
    <h5 class="options__title">
      {{ title || $t('comparator.offers.offer.options') }}
    </h5>
    <ul class="options__ul">
      <li
        v-for="option in options"
        :key="option.id"
        class="options__ul__li">
        <div class="options__ul__li__option">
          <div class="options__ul__li__option__text">
            <span>
              {{ optionLabel(option) }}
              <font-awesome-icon
                v-if="isFormule(option) && option.formule"
                :icon="['fal', 'info-circle']"
                class="options__ul__li__option__text__info"
                @click="openFormuleModal(option.formule)" />
            </span>
            <div
              v-if="displayCheckbox && option.gm_price"
              v-html="$t('comparator.offers.offer.gmPrice', { price: option.gm_price })"
              class="options__ul__li__option__text__gm-price">
            </div>
          </div>
          <div class="options__ul__li__option__input">
            <template v-if="displayCheckbox">
              <el-checkbox
                :model-value="option.is_chosen"
                :disabled="isCheckboxDisabled"
                @change="checkboxClick(option.id)">
                <template v-if="!hidePrice">
                  <span class="options__price">
                    <span
                      v-if="hasOptionDiscount"
                      class="options__price__before">
                      {{ $n(getPriceBeforeDiscount(option), 'currency') }}
                    </span>
                    <span class="options__price__current">
                      {{ $n(getPrice(option), 'currency') }} {{ $t('base.priceType', priceTypeKey) }}
                    </span>
                  </span>
                </template>
              </el-checkbox>
            </template>
            <template v-else>
              <span class="options__price">
                <span
                  v-if="hasOptionDiscount"
                  class="options__price__before">
                  {{ $n(getPriceBeforeDiscount(option), 'currency') }}
                </span>
                <span class="options__price__current">
                  {{ $n(getPrice(option), 'currency') }} {{ $t('base.priceType', priceTypeKey) }}
                </span>
              </span>
            </template>
          </div>
        </div>
      </li>
    </ul>

    <el-dialog
      v-model="isFormuleModalOpen"
      :fullscreen="isFormuleModalFullscreen"
      :show-close="false"
      :append-to-body="true"
      align-center
      class="options__formule-dialog"
      modal-class="center-with-menu"
      width="640px"
      @closed="onFormuleModalClosed">
      <formule-details
        v-if="formuleModalFormule"
        :formule="formuleModalFormule"
        :is-tooltip="true"
        @close="closeFormuleModal" />
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useDemandStore, useOfferStore } from '@/stores';
import offersHelper from '@/utils/helpers/offers';

const FORMULE_MODAL_FULLSCREEN_MAX = 767;

export default {
  name: 'OfferOptions',
  props: {
    options: {
      default: () => [],
      type: Array,
    },
    offerId: {
      required: true,
      type: String,
    },
    offer: {
      required: true,
      type: Object,
    },
    isCheckboxDisabled: {
      type: Boolean,
      default: false,
    },
    hidePrice: Boolean,
    displayCheckbox: Boolean,
    title: String,
  },
  data() {
    return {
      isFormuleModalOpen: false,
      isFormuleModalFullscreen: false,
      formuleModalFormule: null,
    };
  },
  computed: {
    ...mapState(useDemandStore, ['demand']),
    exclTax() {
      return this.demand.show_price_ht;
    },
    priceTypeKey() {
      return this.demand.priceTypeKey;
    },
    hasOptionDiscount() {
      return offersHelper.getPartnerDiscountRate(this.offer) > 0;
    },
  },
  methods: {
    ...mapActions(useOfferStore, ['updateOption']),
    openFormuleModal(formule) {
      this.formuleModalFormule = formule;
      this.isFormuleModalFullscreen = window.innerWidth <= FORMULE_MODAL_FULLSCREEN_MAX;
      this.isFormuleModalOpen = true;
    },
    closeFormuleModal() {
      this.isFormuleModalOpen = false;
    },
    onFormuleModalClosed() {
      this.formuleModalFormule = null;
    },
    isFormule(option) {
      return option.category === 'formule';
    },
    optionLabel(option) {
      return this.isFormule(option)
        ? option.formule?.title
        : option.description;
    },
    getPrice(option) {
      return (this.exclTax ? option.price_ht : option.price) || 0;
    },
    getPriceBeforeDiscount(option) {
      return offersHelper.getOptionPriceBeforeDiscount(option, this.offer, this.exclTax);
    },
    checkboxClick(optionId) {
      this.updateOption({ offerId: this.offerId, optionId });
    },
  },
};
</script>

<style scoped lang="scss">
.options {
  &__ul {
    margin-top: var(--nxt-space-xxxsmall);
    padding-left: 18px;
    font-size: 16px;
    &__li {
      &:not(:last-child) {
        padding-bottom: var(--nxt-space-xxxsmall);
        margin-bottom: var(--nxt-space-xxxsmall);
        border-bottom: 1px solid var(--nxt-color-grey);
      }
      &__option {
        display: flex;
        justify-content: space-between;
        align-items: center;
        &__text {
          margin: auto var(--nxt-space-default) auto 0;
          &__gm-price {
            font-size: 13px;
          }
          &__info {
            margin-left: var(--nxt-space-xxxsmall);
            color: var(--nxt-color-primary);
            cursor: pointer;
          }
        }
        &__input {
          width: 150px;
          min-width: 150px;
          font-weight: 700;
          border-radius: 4px;
          padding: var(--nxt-space-xxxsmall);
          background-color: var(--nxt-color-light-grey);
          :deep(.el-checkbox) {
            display: flex;
            flex-direction: row-reverse;
            justify-content: flex-start;
            margin-right: 0;

            .el-checkbox__label {
              padding-left: var(--nxt-space-xxxsmall);
              padding-right: var(--nxt-space-xxsmall);
              font-weight: 700;
              color: var(--nxt-color-font);
              font-size: 13px;
            }
          }
        }
      }
    }
  }

  &__price {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 3px;
  }

  &__price__before {
    color: var(--nxt-color-grey);
    font-size: 12px;
    font-weight: 600;
    text-decoration: line-through;
  }
}
</style>

<style lang="scss">
// Dialog is teleported to body: styles must be global (unscoped).
.options__formule-dialog.el-dialog {
  border-radius: 10px;
  overflow: hidden;

  &.is-fullscreen {
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }

  .el-dialog__header {
    display: none;
    padding: 0;
    margin: 0;
  }

  .el-dialog__body {
    padding: 0;
    height: 100%;
    box-sizing: border-box;
  }

  &.is-fullscreen .el-dialog__body {
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
  }
}
</style>
